import { CalendarDays, MapPin, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";

const formatDate = (value, withTime = false) => {
  if (!value) return "Date unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return withTime ? date.toLocaleString() : date.toLocaleDateString();
};

const IssuesManagementPanel = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIssue, setSelectedIssue] = useState(null);

  useEffect(() => {
    const fetchIssues = async () => {
      try {
        const res = await api.get("/admin/issues");
        setIssues(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error fetching issues:", err);
        toast.error("Failed to load issues");
      } finally {
        setLoading(false);
      }
    };

    fetchIssues();
  }, []);

  const handleStatusUpdate = async (issueId, newStatus) => {
    try {
      await api.patch(`/admin/issues/${issueId}/status`, { status: newStatus });
      setIssues((prev) => prev.map((issue) => (issue._id === issueId ? { ...issue, status: newStatus } : issue)));
      toast.success(`Issue status updated to ${newStatus}`);
    } catch (err) {
      console.error("Error updating issue:", err);
      toast.error("Failed to update issue status");
    }
  };

  const handleDeleteIssue = async (issueId) => {
    if (!window.confirm("Are you sure you want to delete this issue?")) return;

    try {
      await api.delete(`/admin/issues/${issueId}`);
      setIssues((prev) => prev.filter((issue) => issue._id !== issueId));
      toast.success("Issue deleted successfully");
      setSelectedIssue(null);
    } catch (err) {
      console.error("Error deleting issue:", err);
      toast.error("Failed to delete issue");
    }
  };

  const filteredIssues = issues.filter((issue) => {
    const search = searchTerm.toLowerCase();
    const matchesFilter = filter === "all" || issue.status === filter;
    const matchesSearch =
      (issue.title || "").toLowerCase().includes(search) ||
      (issue.description || "").toLowerCase().includes(search) ||
      (issue.constituency || "").toLowerCase().includes(search);
    return matchesFilter && matchesSearch;
  });

  const statusColors = {
    Open: "bg-[#eef4ff] text-[#2f6fed]",
    "Under Review": "bg-[#fff1df] text-[#b76500]",
    Resolved: "bg-[#edf8ef] text-[#106d06]",
    Flagged: "bg-[#fff0ec] text-[#c44c29]",
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#ff9933]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Issues</h2>
          <p className="text-sm text-slate-500">Live management for issue moderation, progress, and cleanup.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <input
            type="text"
            placeholder="Search issues..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm focus:border-[#ff9933] focus:outline-none focus:ring-2 focus:ring-[#ffe1b8] sm:w-72"
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm focus:border-[#ff9933] focus:outline-none focus:ring-2 focus:ring-[#ffe1b8]"
          >
            <option value="all">All Status</option>
            <option value="Open">Open</option>
            <option value="Under Review">Under Review</option>
            <option value="Resolved">Resolved</option>
            <option value="Flagged">Flagged</option>
          </select>
        </div>
      </div>

      <div className="grid gap-4">
        {filteredIssues.map((issue, index) => (
          <motion.div
            key={issue._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.03 }}
            className="rounded-[1.75rem] border border-[#d7dfd4] bg-white p-5 shadow-sm"
          >
            <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusColors[issue.status] || "bg-slate-100 text-slate-700"}`}>
                    {issue.status || "Open"}
                  </span>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                    {issue.category || "Other"}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                    <CalendarDays className="h-3.5 w-3.5" />
                    {formatDate(issue.createdAt)}
                  </span>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900">{issue.title || "Untitled issue"}</h3>
                  <p className="mt-1 line-clamp-2 text-sm leading-6 text-slate-600">{issue.description || "No description provided."}</p>
                </div>

                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#fff1df] px-3 py-1 text-[#b76500]">
                    <MapPin className="h-3 w-3" />
                    {issue.constituency || "Unknown constituency"}
                  </span>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-600">{issue.district || "Unknown district"}</span>
                  <span className="rounded-full bg-[#edf8ef] px-3 py-1 text-[#106d06]">{issue.state || "Unknown state"}</span>
                  {issue.assignedToName && (
                    <span className="rounded-full bg-[#eef4ff] px-3 py-1 text-[#2f6fed]">{issue.assignedToName}</span>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-3 xl:w-52">
                <button
                  onClick={() => setSelectedIssue(issue)}
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
                >
                  View Details
                </button>
                <select
                  value={issue.status}
                  onChange={(e) => handleStatusUpdate(issue._id, e.target.value)}
                  className="rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm focus:border-[#ff9933] focus:outline-none focus:ring-2 focus:ring-[#ffe1b8]"
                >
                  <option value="Open">Open</option>
                  <option value="Under Review">Under Review</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Flagged">Flagged</option>
                </select>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredIssues.length === 0 && (
        <div className="rounded-[1.75rem] border border-dashed border-slate-200 bg-slate-50 p-12 text-center text-slate-500">
          No issues found for the current filters.
        </div>
      )}

      {selectedIssue && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setSelectedIssue(null)}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[2rem] bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-slate-100 bg-gradient-to-r from-[#fff1df] via-white to-[#edf8ef] px-6 py-5">
              <h3 className="text-xl font-semibold text-slate-900">Issue Details</h3>
              <p className="mt-1 text-sm text-slate-500">Administrative case view</p>
            </div>
            <div className="space-y-5 p-6">
              <div>
                <p className="text-sm font-medium text-slate-500">Title</p>
                <p className="text-lg font-semibold text-slate-900">{selectedIssue.title || "Untitled issue"}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Description</p>
                <p className="text-slate-700">{selectedIssue.description || "No description provided."}</p>
              </div>
              <div className="grid gap-4 text-sm text-slate-700 sm:grid-cols-2">
                <p>Category: {selectedIssue.category || "N/A"}</p>
                <p>Status: {selectedIssue.status || "N/A"}</p>
                <p>Constituency: {selectedIssue.constituency || "N/A"}</p>
                <p>District: {selectedIssue.district || "N/A"}</p>
                <p>State: {selectedIssue.state || "N/A"}</p>
                <p>Assigned To: {selectedIssue.assignedToName || "Not assigned"}</p>
              </div>
              {selectedIssue.comments?.length > 0 && (
                <div>
                  <h4 className="mb-3 font-semibold text-slate-900">Comments</h4>
                  <div className="space-y-2">
                    {selectedIssue.comments.map((comment, index) => (
                      <div key={index} className="rounded-3xl bg-slate-50 p-4 text-sm text-slate-700">
                        <p>{comment.text || "No comment text"}</p>
                        <p className="mt-2 text-xs text-slate-400">{formatDate(comment.date, true)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <button
                onClick={() => handleDeleteIssue(selectedIssue._id)}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#d95d39] px-4 py-3 font-medium text-white transition-colors hover:bg-[#c44c29]"
              >
                <Trash2 className="h-4 w-4" />
                Delete Issue
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default IssuesManagementPanel;
